import { useState, useEffect } from "react";
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from "@component/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@component/tabs";
import {
    Sparkles,
    MessageCircleIcon,
    AlertCircleIcon,
    CheckCircleIcon,
    VideoIcon,
    MicIcon,
    FileQuestionIcon,
    InboxIcon,
} from "lucide-react";
import {
    getCandidateInsights,
    isClassicGlobalInsights,
    isPracticeInterviewInsights,
    InsightItem,
    Insight,
} from "@service/interview";
import FeedbackOverview from "@container/feedback-overview";
import { FeedbackItem } from "@type/interview";
import { format } from "date-fns";
import { interviewStatusPendingInterview } from "@constant/app";

type ScoredView = {
    kind: "scored";
    score: number | null;
    feedbacks: FeedbackItem[];
};

type ClassicView = {
    kind: "classic";
    videoQuality: InsightItem[];
    audioQuality: InsightItem[];
    insights: Insight[];
};

type ViewState = ScoredView | ClassicView | { kind: "empty" };

export interface CandidateAIInterviewInsightsProps {
    conversation_id: string;
    tips?: string[];
    interviewDate?: string;
    height?: string;
    insightsHeight?: string;
    interviewStatus?: string;
}

export default function CandidateAIInterviewInsights({
    conversation_id,
    interviewStatus,
    height = "450px",
    insightsHeight = "300px",
    tips: initialTips = [],
}: CandidateAIInterviewInsightsProps) {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [currentTipIndex, setCurrentTipIndex] = useState(0);
    const [view, setView] = useState<ViewState>({ kind: "empty" });
    const [interviewDate, setInterviewDate] = useState<string>(
        format(new Date(), "d MMMM yyyy")
    );
    const [tips] = useState<string[]>(
        initialTips.length > 0
            ? initialTips
            : [
                "Analyzing your communication patterns...",
                "Reviewing your technical responses...",
                "Evaluating your problem-solving approach...",
                "Assessing your collaboration style...",
            ]
    );

    useEffect(() => {
        const fetchInsights = async () => {
            if (!conversation_id) {
                setLoading(true);
                return;
            }

            try {
                setLoading(true);
                const response = await getCandidateInsights(conversation_id);

                if (
                    !response ||
                    !(
                        response.status?.includes("partial_success") ||
                        response.status?.includes("success")
                    )
                ) {
                    return;
                }

                const global = response.global_video_insights;

                if (isPracticeInterviewInsights(global)) {
                    setView({
                        kind: "scored",
                        score: typeof global.score === "number" ? global.score : null,
                        feedbacks: global.feedbacks ?? [],
                    });
                } else if (isClassicGlobalInsights(global)) {
                    const sortedInsights = (global.globalInsights ?? [])
                        .map((i) => ({ type: i.type as "positive" | "suggestion", content: i.content }))
                        .sort((a, b) => {
                            if (a.type === "positive" && b.type === "suggestion") return -1;
                            if (a.type === "suggestion" && b.type === "positive") return 1;
                            return 0;
                        });
                    setView({
                        kind: "classic",
                        videoQuality: global.globalVideoQuality ?? [],
                        audioQuality: global.globalAudioQuality ?? [],
                        insights: sortedInsights,
                    });
                } else {
                    setView({ kind: "empty" });
                }

                setInterviewDate(format(new Date(), "d MMMM yyyy"));
                setLoading(false);
            } catch (err) {
                console.error("Error fetching interview insights:", err);
                setError("An error occurred while fetching interview insights.");
                setLoading(false);
            }
        };

        fetchInsights();
    }, [conversation_id]);

    useEffect(() => {
        if (loading && tips.length > 0) {
            const interval = setInterval(() => {
                setCurrentTipIndex((prevIndex) => (prevIndex + 1) % tips.length);
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [loading, tips.length]);

    if (loading && interviewStatus !== interviewStatusPendingInterview) {
        return (
            <Card
                className="w-full mb-6 border-blue-100 dark:border-blue-900 shadow-sm"
                style={{ height }}
            >
                <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                            <Sparkles className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
                            <CardTitle className="font-medium text-gray-900 dark:text-white">
                                AI Interview Insights
                            </CardTitle>
                        </h4>
                    </div>
                </CardHeader>
                <CardContent className="flex items-center justify-center h-[calc(100%-70px)]">
                    <div className="flex flex-col items-center justify-center py-4">
                        <div className="flex space-x-2 mb-4">
                            <div className="h-3 w-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                            <div className="h-3 w-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                            <div className="h-3 w-3 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: "600ms" }} />
                        </div>
                        <p className="text-center text-sm font-medium mb-4">
                            Analyzing your interview...
                        </p>
                        <div className="max-w-md bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md border border-blue-100 dark:border-blue-800 shadow-sm transition-all duration-300 hover:shadow-md">
                            <div className="flex items-start">
                                <div className="mr-2 mt-0.5 flex-shrink-0">
                                    <MessageCircleIcon className="h-4 w-4 text-blue-500" />
                                </div>
                                <p className="text-sm text-blue-700 dark:text-blue-300">
                                    <span className="font-medium">Tip:</span>{" "}
                                    {tips[currentTipIndex] || "Preparing insights for your interview..."}
                                </p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (error) {
        return (
            <Card className="w-full mb-6 border-blue-100 dark:border-blue-900 shadow-sm">
                <CardHeader className="pb-2">
                    <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                            <Sparkles className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400" />
                            <CardTitle className="font-medium text-gray-900 dark:text-white">
                                AI Interview Insights
                            </CardTitle>
                        </h4>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-col items-center justify-center py-8">
                        <AlertCircleIcon className="h-10 w-10 text-red-500 mb-4" />
                        <p className="text-center text-sm font-medium">{error}</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card
            className="w-full mb-6 border border-gray-200 rounded-md"
            style={{ height: loading ? height : "auto" }}
        >
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
                        <Sparkles className="h-5 w-5 mr-2 text-blue-500 dark:text-blue-400 flex-shrink-0" />
                        <CardTitle className="font-medium text-gray-900 dark:text-white">
                            AI Interview Insights
                        </CardTitle>
                    </h4>
                </div>
                {view.kind === "scored" && view.score !== null && (
                    <div className="flex items-baseline gap-1 text-sm text-gray-700 dark:text-gray-200">
                        <span className="font-medium">Overall score:</span>
                        <span className="text-base font-bold text-blue-600 tabular-nums">
                            {view.score.toFixed(1)}
                        </span>
                        <span className="text-xs text-gray-500">/ 10</span>
                    </div>
                )}
            </CardHeader>
            <CardContent className="flex flex-col">
                {view.kind === "scored" ? (
                    <div
                        className="mb-4 flex-grow overflow-hidden"
                        style={{ maxHeight: insightsHeight, overflowY: "auto" }}
                    >
                        {view.feedbacks.length > 0 ? (
                            <FeedbackOverview feedback={view.feedbacks} />
                        ) : (
                            <EmptyInsights />
                        )}
                    </div>
                ) : view.kind === "classic" ? (
                    <ClassicTabs
                        view={view}
                        insightsHeight={insightsHeight}
                    />
                ) : (
                    <div className="mb-4 flex-grow flex items-center justify-center">
                        <EmptyInsights />
                    </div>
                )}

                {interviewStatus !== interviewStatusPendingInterview && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-800">
                        <div className="flex items-center" />
                        <span>Analyzed on {interviewDate}</span>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

function EmptyInsights() {
    return (
        <div className="flex h-full items-center justify-center">
            <div className="py-8 flex flex-col items-center justify-center text-center">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-full mb-4">
                    <FileQuestionIcon className="h-6 w-6 text-blue-500" />
                </div>
                <h3 className="text-lg font-medium mb-2">No insights available.</h3>
                <p className="text-gray-500 dark:text-gray-400 max-w-md">
                    We haven’t generated any interview insights yet. <br />
                    Complete your interview to receive personalized feedback.
                </p>
            </div>
        </div>
    );
}

function EmptyTechnical() {
    return (
        <div className="flex h-full items-center justify-center">
            <div className="py-8 flex flex-col items-center justify-center text-center">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-full mb-4">
                    <InboxIcon className="h-6 w-6 text-blue-500" />
                </div>
                <h3 className="text-lg font-medium mb-2">No technical data available.</h3>
                <p className="text-gray-500 dark:text-gray-400 max-w-md">
                    We haven't analyzed your video and audio quality yet.
                    Complete your interview to receive technical feedback.
                </p>
            </div>
        </div>
    );
}

function ClassicTabs({
    view,
    insightsHeight,
}: {
    view: ClassicView;
    insightsHeight: string;
}) {
    return (
        <div className="mb-4 flex-grow overflow-hidden">
            <Tabs defaultValue="insights" className="w-full flex flex-col">
                <TabsList className="grid w-full grid-cols-2 mb-2">
                    <TabsTrigger value="insights" className="text-xs sm:text-sm">
                        <CheckCircleIcon className="h-3 w-3 mr-1 sm:mr-2 flex-shrink-0" />
                        Interview Insights
                    </TabsTrigger>
                    <TabsTrigger value="technical" className="text-xs sm:text-sm">
                        <VideoIcon className="h-3 w-3 mr-1 sm:mr-2 flex-shrink-0" />
                        Technical Quality
                    </TabsTrigger>
                </TabsList>

                <TabsContent
                    value="insights"
                    className="overflow-hidden"
                    style={{ height: insightsHeight }}
                >
                    <div className="space-y-3 h-full overflow-y-auto pr-1">
                        {view.insights.length > 0 ? (
                            view.insights.map((insight, index) => (
                                <div
                                    key={index}
                                    className={`p-3 rounded-md ${insight.type === "positive"
                                        ? "bg-blue-50 dark:bg-blue-900/20"
                                        : "bg-amber-50"
                                        }`}
                                >
                                    <div className="flex items-start gap-2">
                                        {insight.type === "positive" ? (
                                            <CheckCircleIcon className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                                        ) : (
                                            <AlertCircleIcon className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                                        )}
                                        <p
                                            className={`text-sm ${insight.type !== "positive" ? "text-amber-800" : "text-blue-800"
                                                }`}
                                        >
                                            {insight.content}
                                        </p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <EmptyInsights />
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="technical" className="overflow-y-visible">
                    {view.videoQuality.length > 0 || view.audioQuality.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                    <VideoIcon className="h-4 w-4 text-blue-500" />
                                    <h3 className="font-medium">Video Quality</h3>
                                </div>
                                <div className="space-y-3 pl-6">
                                    {view.videoQuality.length > 0 ? (
                                        view.videoQuality.map((item, index) => (
                                            <div key={index} className="space-y-1">
                                                <h4 className="text-sm font-medium">{item.title}</h4>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    {item.description}
                                                </p>
                                            </div>
                                        ))
                                    ) : (
                                        <EmptyTechnical />
                                    )}
                                </div>
                            </div>
                            <div className="space-y-3">
                                <div className="flex items-center gap-2">
                                    <MicIcon className="h-4 w-4 text-blue-500" />
                                    <h3 className="font-medium">Audio Quality</h3>
                                </div>
                                <div className="space-y-3 pl-6">
                                    {view.audioQuality.length > 0 ? (
                                        view.audioQuality.map((item, index) => (
                                            <div key={index} className="space-y-1">
                                                <h4 className="text-sm font-medium">{item.title}</h4>
                                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                                    {item.description}
                                                </p>
                                            </div>
                                        ))
                                    ) : (
                                        <EmptyTechnical />
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <EmptyTechnical />
                    )}
                </TabsContent>
            </Tabs>
        </div>
    );
}
